import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, FileText, Loader2, Clock, Gauge, MapPin, Bike } from 'lucide-react';
import { DRIVER_HEX } from '../../components/chauffeur/LiveMap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const todayStr = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' });
const shiftDay = (d, n) => {
  const x = new Date(`${d}T12:00:00`);
  x.setDate(x.getDate() + n);
  return x.toLocaleDateString('sv-SE');
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-zinc-50 rounded-lg px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Icon className="w-3 h-3" />{label}</div>
    <div className="text-lg font-bold text-zinc-900" data-testid={testid}>{value}</div>
  </div>
);

const DriverSummary = ({ s }) => (
  <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid={`trip-driver-${s.driver}`}>
    <div className="h-1.5" style={{ background: DRIVER_HEX[s.driver] || '#7c3aed' }} />
    <div className="p-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-bold text-zinc-900">{s.driver}</h2>
        <span className="text-xs text-zinc-500">{s.gps_points} gps-punten</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
        <Stat icon={Clock} label="Start – eind" value={`${s.start_time} – ${s.end_time}`} testid={`trip-times-${s.driver}`} />
        <Stat icon={Clock} label="Rijtijd" value={s.duration_label} testid={`trip-duration-${s.driver}`} />
        <Stat icon={Gauge} label="Kilometers" value={`${s.km_total} km`} testid={`trip-km-${s.driver}`} />
        <Stat icon={Bike} label="Opgehaald" value={`${s.motor_count} motoren · ${s.stop_count} stops`} testid={`trip-motors-${s.driver}`} />
      </div>
      {s.km_gap > 0 && (
        <p className="text-[11px] text-zinc-500 mt-1">Waarvan {s.km_gap} km hemelsbreed geschat (pagina was even dicht).</p>
      )}
      <div className="mt-4 space-y-2">
        {s.stops.map((st, i) => (
          <div key={i} className="border-l-2 border-purple-300 pl-3">
            <div className="text-sm font-semibold text-zinc-900">{st.company}</div>
            {st.address && <div className="text-xs text-zinc-500">{st.address}</div>}
            {st.motors.map((m, j) => (
              <div key={j} className="text-sm text-zinc-700 flex items-center gap-2 mt-0.5">
                <span className="font-mono text-xs text-purple-700 w-10">{m.time}</span>
                <span>{m.label}</span>
                {m.license_plate && <span className="text-xs text-zinc-500">({m.license_plate})</span>}
                {m.location && (
                  <a href={`https://www.google.com/maps?q=${m.location.lat},${m.location.lon}`} target="_blank" rel="noopener noreferrer"
                     className="text-xs text-purple-600 hover:underline flex items-center gap-0.5">
                    <MapPin className="w-3 h-3" />plek
                  </a>
                )}
              </div>
            ))}
          </div>
        ))}
        {s.stops.length === 0 && <p className="text-sm text-zinc-500">Geen motoren opgehaald.</p>}
      </div>
      {s.not_picked?.length > 0 && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs text-amber-900" data-testid={`trip-not-picked-${s.driver}`}>
          <b>Nog niet opgehaald:</b> {s.not_picked.map((n) => `${n.company} (${n.motors.length})`).join(', ')}
        </div>
      )}
    </div>
  </div>
);

export default function AdminTripSummary() {
  const token = localStorage.getItem('token');
  const [day, setDay] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/trip-summary?date=${d}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ritoverzicht laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(day); }, [day, load]);

  const openPdf = async () => {
    const win = window.open('', '_blank');
    try {
      const res = await axios.get(`${API}/admin/trip-summary/pdf?date=${day}`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (win) win.location.href = url; else window.location.href = url;
    } catch (e) {
      if (win) win.close();
      toast.error('PDF maken mislukt');
    }
  };

  const isToday = day === todayStr();
  return (
    <div className="min-h-screen bg-zinc-50 pb-16">
      <div className="max-w-5xl mx-auto p-4 space-y-4">
        <Link to="/admin/chauffeur-route" className="flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900" data-testid="trip-back">
          <ArrowLeft className="w-4 h-4" /> Terug naar Chauffeur Route
        </Link>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-zinc-900" data-testid="trip-title">Ritoverzicht</h1>
            <p className="text-sm text-zinc-500">Elke avond om 20:00 ook per e-mail. Kilometers zijn een schatting op basis van gps.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setDay(shiftDay(day, -1))} className="p-2 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-100" data-testid="trip-prev-day">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <input type="date" value={day} max={todayStr()} onChange={(e) => e.target.value && setDay(e.target.value)}
                   className="border border-zinc-300 rounded-lg px-3 py-1.5 text-sm bg-white" data-testid="trip-date" />
            <button onClick={() => setDay(shiftDay(day, 1))} disabled={isToday} className="p-2 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-100 disabled:opacity-40" data-testid="trip-next-day">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button onClick={openPdf} disabled={!data?.drivers?.length} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold disabled:opacity-40" data-testid="trip-pdf">
              <FileText className="w-4 h-4" /> PDF
            </button>
          </div>
        </div>
        {loading && <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-purple-600 animate-spin" /></div>}
        {!loading && data?.drivers?.length === 0 && (
          <div className="bg-white rounded-xl border border-zinc-200 p-12 text-center text-zinc-500" data-testid="trip-empty">
            Geen ritten op {new Date(`${day}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}.
          </div>
        )}
        {!loading && data?.drivers?.map((s) => <DriverSummary key={s.driver} s={s} />)}
      </div>
    </div>
  );
}
